/* ==========================================================================================
   Blenders Online 2026 · bo-header.js · behaviour of sections/bo-header.liquid (header builder)
   Deferred, idempotent, re-binds on shopify:section:load. Needs nothing but the markup; uses window.BO
   (bo-main.js) when it is there, for the tabs inside a copied panel.
     - the compact sticky bar (with a gap between the "on" and "off" scroll points, so it never flickers)
     - the mega menus: hover with intent, click / Enter on the caret, Escape, click outside, the dimmed page,
       photos asked for only once the pointer or keyboard reaches the navigation
     - the navigation fits itself: gaps tighten, then the sister store's short label, then the social icons step out
     - the phone drawer: level 2 is copied from the computer panel on first use (the menu is in the page once),
       columns become accordions, focus is kept inside, Escape closes
     - the phone search row starts tucked away on every page and tucks away after scroll; the magnifier in the bar
       brings it back, and a second tap or Escape tucks it away again (Tommy 02-10)
     - no layout is read on a scroll frame: the compact switch listens to two page markers (IntersectionObserver),
       the header height comes from a ResizeObserver
     - one swirl of the logo when something goes into the cart
   ========================================================================================== */
(() => {
  'use strict';
  if (window.BOHeader) { window.BOHeader.init(document); return; }

  const W = window, D = document, H = D.documentElement;
  const mqDesk = W.matchMedia('(min-width: 1100px)');
  const mqPhone = W.matchMedia('(max-width: 749px)');
  const mqReduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const ON = 140, OFF = 24;              // compact on below 140px scrolled, off again above 24px
  const OPEN_DELAY = 140, SWITCH_DELAY = 60, CLOSE_DELAY = 260;

  const ICON_CHEV_LEFT = '<svg class="bo-icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5.5L8.5 12l6.5 6.5"/></svg>';
  const ICON_PLUS = '<svg class="bo-icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>';

  const bind = (hdr) => {
    if (hdr.boBound) return;
    hdr.boBound = true;
    const bars = hdr.querySelector('.bo-hdr__bars');
    const nav = hdr.querySelector('[data-bo-nav]');
    const scrim = hdr.querySelector('[data-bo-scrim]');
    const items = [...hdr.querySelectorAll('.bo-nav__item[data-bo-item]')];

    /* ---------- header height, for the panels and for anyone else who needs it ----------
       (fix-chrome 01-10, critic finding 19: nothing here reads layout on a scroll frame any more.) The height comes
       from a ResizeObserver (the browser hands over the size it has already worked out), written to :root only when
       it changed and only once the header has stopped changing (a custom property on :root restyles the whole page).
       Where the bar ends (--bo-hdr-bottom, the tallest a panel may be) is read only when a panel opens. */
    let lastH = -1, hT = 0;
    const writeH = (h) => {
      h = Math.round(h);
      if (h === lastH || h < 1) return;
      lastH = h;
      H.style.setProperty('--bo-header-h', h + 'px');
      H.style.setProperty('--header-height', h + 'px');
    };
    const measure = () => {
      const r = bars.getBoundingClientRect();
      writeH(r.height);
      hdr.style.setProperty('--bo-hdr-bottom', Math.max(0, Math.round(r.bottom)) + 'px');
    };
    if (W.ResizeObserver) {
      let first = true;
      new ResizeObserver((entries) => {
        const e = entries[entries.length - 1];
        const box = e.borderBoxSize && (e.borderBoxSize[0] || e.borderBoxSize);
        const h = box && box.blockSize ? box.blockSize : e.contentRect.height;
        clearTimeout(hT);
        if (first) { first = false; writeH(h); return; }
        hT = setTimeout(() => writeH(h), 90);
      }).observe(bars);
    }

    /* ---------- photos: asked for once the navigation is reached ---------- */
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      hdr.querySelectorAll('.bo-mega img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    };

    /* ---------- mega menus ---------- */
    let openItem = null, openT = 0, closeT = 0;
    const panelOf = (it) => it.querySelector('[data-bo-panel]');
    const setExpanded = (it, on) => it.querySelectorAll('[data-bo-toggle]').forEach((b) => b.setAttribute('aria-expanded', on ? 'true' : 'false'));
    const show = (it, instant) => {
      const p = panelOf(it);
      if (!p) return;
      measure();
      p.hidden = false;
      if (instant || mqReduce.matches) { p.classList.add('is-open'); return; }
      void p.offsetWidth;
      p.classList.add('is-open');
    };
    const hide = (it, instant) => {
      const p = panelOf(it);
      if (!p) return;
      p.classList.remove('is-open');
      const done = () => { if (!p.classList.contains('is-open')) p.hidden = true; };
      if (instant || mqReduce.matches) done(); else setTimeout(done, 320);
    };
    const open = (it) => {
      clearTimeout(openT); clearTimeout(closeT);
      if (openItem === it) return;
      const switching = !!openItem;
      hdr.classList.toggle('is-switching', switching);
      if (openItem) { openItem.classList.remove('is-open'); setExpanded(openItem, false); hide(openItem, true); }
      openItem = it;
      it.classList.add('is-open');
      setExpanded(it, true);
      warm();
      show(it, switching);
      /* every panel, the Support list too, dims the page behind it (critic finding 48: one menu system) */
      hdr.classList.add('is-mega-open', 'is-any-open');
    };
    const close = (focusBack) => {
      clearTimeout(openT); clearTimeout(closeT);
      if (!openItem) return;
      const it = openItem;
      openItem = null;
      it.classList.remove('is-open');
      setExpanded(it, false);
      hide(it, false);
      hdr.classList.remove('is-mega-open', 'is-any-open', 'is-switching');
      if (focusBack) { const t = it.querySelector('[data-bo-toggle]'); if (t) t.focus(); }
    };
    items.forEach((it) => {
      it.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !mqDesk.matches) return;
        clearTimeout(closeT); clearTimeout(openT);
        openT = setTimeout(() => open(it), openItem ? SWITCH_DELAY : OPEN_DELAY);
      });
      it.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openT);
        closeT = setTimeout(() => { if (openItem === it) close(false); }, CLOSE_DELAY);
      });
      it.querySelectorAll('[data-bo-toggle]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        if (openItem === it) close(false); else open(it);
      }));
      /* a touch screen at computer width: the first tap on a label opens its panel, the second follows the link */
      const top = it.querySelector('a[data-bo-top]');
      if (top) {
        let lastPointer = 'mouse';
        top.addEventListener('pointerdown', (e) => { lastPointer = e.pointerType; });
        top.addEventListener('click', (e) => {
          if (lastPointer === 'mouse' || !mqDesk.matches) return;
          if (openItem !== it) { e.preventDefault(); open(it); }
        });
        /* keyboard: arrow down on a label opens its panel and moves into it */
        top.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowDown') return;
          e.preventDefault();
          open(it);
          const first = panelOf(it) && panelOf(it).querySelector('a[href], button');
          if (first) setTimeout(() => first.focus(), 30);
        });
      }
      it.addEventListener('focusout', (e) => {
        if (openItem === it && e.relatedTarget && !it.contains(e.relatedTarget)) close(false);
      });
    });
    if (nav) {
      nav.addEventListener('pointerenter', warm, { once: true });
      nav.addEventListener('focusin', warm, { once: true });
    }
    /* the pointer reaching the header at all (on its way to the navigation) is intent enough to start them */
    if (bars) bars.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && mqDesk.matches) warm(); }, { once: true });
    /* a mouse moving on the page at computer width is a person, not a crawler: start the (small) menu photos
       shortly after, so a panel opened a moment later is already filled */
    const onMove = (e) => {
      if (e.pointerType !== 'mouse' || !mqDesk.matches) return;
      D.removeEventListener('pointermove', onMove);
      setTimeout(warm, 600);
    };
    D.addEventListener('pointermove', onMove, { passive: true });
    if (scrim) scrim.addEventListener('click', () => close(false));
    D.addEventListener('click', (e) => { if (openItem && !openItem.contains(e.target)) close(false); });
    D.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openItem) close(true); });

    /* ---------- the navigation fits itself (computer width) ---------- */
    const list = nav && nav.querySelector('.bo-nav');
    const fit = () => {
      if (!list || !mqDesk.matches) return;
      /* what gives, in order, while the row is too long: the gaps, the sister store's long name, the social icons,
         the gaps again, the sister store's name altogether (its icon stays, with its name for screen readers and
         as a tooltip), and only then the text size, a little (16px, never smaller) */
      const steps = ['is-nav-tight', 'is-nav-short', 'is-nav-nosocial', 'is-nav-tighter', 'is-nav-iconly', 'is-nav-small'];
      hdr.classList.remove(...steps);
      const over = () => list.scrollWidth > list.clientWidth + 1;
      for (const s of steps) { if (!over()) break; hdr.classList.add(s); }
    };

    /* ---------- compact bar + the phone search row ----------
       (fix-chrome 01-10, critic finding 19) The compact switch no longer reads window.scrollY on a scroll frame
       (that read forced a layout of the whole page, right after the rails had written their styles: 0.8 s on a
       slow phone). Two invisible 1px markers sit in the page at 140px and 24px from the top, and an
       IntersectionObserver says when they leave or come back: compact on once the page has scrolled past 140px,
       off again above 24px (the same gap as before, so it never flickers). The scroll listener that is left does
       no layout reads at all: it only lets a tucked-away search row catch up once the search box is no longer in use.
       (finding 5) On product pages a phone starts with the search row tucked away (the page's server markup
       already carries is-tucked, so nothing jumps); the search button in the bar brings it back. Tommy 02-10: every
       page now starts that way, and the magnifier says whether its row is showing (aria-expanded). */
    const sticky = hdr.dataset.sticky !== 'false';
    const tuckStart = hdr.hasAttribute('data-bo-tuck-start');
    let compact = false, tucked = hdr.classList.contains('is-tucked'), ticking = false, searchOpen = false;
    const input = hdr.querySelector('.bo-hdr__search input[type="search"]');
    const findBtn = hdr.querySelector('[data-bo-search-open]');
    const drawerIsOpen = () => D.body.classList.contains('bo-drawer-open');
    const busy = () => !!(input && (D.activeElement === input || input.value));
    const syncFind = () => { if (findBtn) findBtn.setAttribute('aria-expanded', mqPhone.matches && !tucked ? 'true' : 'false'); };
    const setTucked = (on) => {
      if (on === tucked) return;
      tucked = on;
      hdr.classList.toggle('is-tucked', on);
      syncFind();
    };
    const syncTuck = () => {
      if (!hdr.isConnected || drawerIsOpen()) return;
      if (!mqPhone.matches) { setTucked(false); searchOpen = false; return; }
      if ((compact || tuckStart) && !searchOpen && !busy()) setTucked(true);
      else if (!compact && !tuckStart) { setTucked(false); searchOpen = false; }
    };
    const setCompact = (on) => {
      if (on === compact) return;
      compact = on;
      hdr.classList.toggle('is-compact', on);
      syncTuck();
    };
    if (sticky) {
      if (W.IntersectionObserver) {
        const mark = (top) => {
          const m = D.createElement('i');
          m.setAttribute('aria-hidden', 'true');
          m.setAttribute('data-bo-hdr-mark', top);
          m.style.cssText = 'position:absolute;left:0;top:' + top + 'px;width:1px;height:1px;pointer-events:none;visibility:hidden;';
          D.body.appendChild(m);
          return m;
        };
        D.querySelectorAll('[data-bo-hdr-mark]').forEach((m) => m.remove());   /* a theme-editor reload re-binds */
        const mOn = mark(ON), mOff = mark(OFF);
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.target === mOn && !e.isIntersecting && e.boundingClientRect.top < 0) setCompact(true);
            else if (e.target === mOff && e.isIntersecting) setCompact(false);
          });
        });
        io.observe(mOn);
        io.observe(mOff);
      } else {
        /* very old browsers only: the plain scroll check */
        W.addEventListener('scroll', () => {
          const y = W.scrollY;
          if (!compact && y > ON) setCompact(true); else if (compact && y < OFF) setCompact(false);
        }, { passive: true });
      }
    }
    /* a row that could not tuck (the search box was in use) catches up on the next scroll; no layout is read */
    const onScroll = () => {
      if (ticking || tucked || searchOpen || !(compact || tuckStart) || !mqPhone.matches) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; syncTuck(); });
    };
    W.addEventListener('scroll', onScroll, { passive: true });
    /* Tommy 02-10 ("only want the magnifying glass icon"): the magnifier stays in the bar while its row is open, so a
       second tap tucks the row away again, as Escape does (focus back on the magnifier). The box is focused in the same
       tap, so a phone's keyboard comes up with it (a focus a moment later can leave an iPhone's keyboard down). */
    if (findBtn) findBtn.addEventListener('click', () => {
      if (mqPhone.matches && !tucked) { searchOpen = false; setTucked(true); return; }
      searchOpen = true;
      setTucked(false);
      if (!input) return;
      input.focus({ preventScroll: true });
      if (D.activeElement !== input) setTimeout(() => input.focus(), 60);
    });
    hdr.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || tucked || !findBtn || !mqPhone.matches || !e.target.closest('[data-bo-search], [data-bo-search-open]')) return;
      searchOpen = false;
      setTucked(true);
      findBtn.focus();
    });
    // phone review 02-10: opened from that button and then left empty, the row tucks away again on the next scroll
    // (it stayed open, 126px tall, for the rest of the page)
    if (input) input.addEventListener('blur', () => {
      setTimeout(() => { if (searchOpen && !busy() && !hdr.contains(D.activeElement)) searchOpen = false; }, 200);
    });

    /* the phone gets the shorter search hint */
    const hint = () => {
      if (!input) return;
      const h = mqDesk.matches || !mqPhone.matches ? input.dataset.hint : (input.dataset.hintM || input.dataset.hint);
      if (h && input.placeholder !== h) input.placeholder = h;
    };

    /* ---------- the drawer ---------- */
    const drawer = hdr.querySelector('[data-bo-drawer]');
    const dPanel = drawer && drawer.querySelector('[data-bo-drawer-panel]');
    const views = drawer && drawer.querySelector('[data-bo-views]');
    const rootView = views && views.querySelector('[data-bo-view="root"]');
    let dOpen = false, lastFocus = null, hideT = 0, logoDone = false, current = rootView, opener = null;
    const fillLogo = () => {
      if (logoDone || !drawer) return;
      logoDone = true;
      const slot = drawer.querySelector('[data-bo-logo-slot]');
      const logo = hdr.querySelector('.bo-hdr__logo .bo-logo');
      if (slot && logo) {
        const c = logo.cloneNode(true);
        c.classList.remove('bo-logo--animate');
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('role');
        c.removeAttribute('aria-label');
        c.querySelectorAll('[mask]').forEach((m) => m.removeAttribute('mask'));
        c.querySelectorAll('mask').forEach((m) => m.remove());
        slot.appendChild(c);
      }
    };
    /* copy a computer panel into the drawer, turn its columns into accordions */
    let uid = 0;
    const buildView = (key) => {
      let v = views.querySelector('[data-bo-view="' + key + '"]');
      if (v) return v;
      const row = rootView.querySelector('[data-bo-go="' + key + '"]');
      const src = hdr.querySelector('.bo-hdr__nav [data-bo-panel="' + key + '"]');
      if (!src) return null;
      v = D.createElement('div');
      v.className = 'bo-drawer__view';
      v.setAttribute('data-bo-view', key);
      const sub = D.createElement('div');
      sub.className = 'bo-drawer__sub';
      const back = D.createElement('button');
      back.type = 'button';
      back.className = 'bo-drawer__back';
      back.innerHTML = ICON_CHEV_LEFT + '<span>' + (drawer.dataset.backLabel || 'Menu') + '</span>';
      back.addEventListener('click', () => go(null));
      const title = D.createElement('p');
      title.className = 'bo-drawer__title';
      title.textContent = row ? row.querySelector('.bo-drawer__rowtext').textContent : '';
      sub.append(back, title);
      v.appendChild(sub);
      const copy = src.cloneNode(true);
      copy.hidden = false;
      copy.classList.remove('is-open');
      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach((el) => {
        const nid = el.id + '-d';
        copy.querySelectorAll('[aria-controls="' + el.id + '"]').forEach((a) => a.setAttribute('aria-controls', nid));
        copy.querySelectorAll('[aria-labelledby="' + el.id + '"]').forEach((a) => a.setAttribute('aria-labelledby', nid));
        el.id = nid;
      });
      copy.querySelectorAll('[data-bo-bound], [data-bo-b]').forEach((el) => { el.removeAttribute('data-bo-bound'); el.removeAttribute('data-bo-b'); });
      copy.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      /* accordions: every column but the photo tiles and the deal tile */
      copy.querySelectorAll('[data-bo-mcol]').forEach((col) => {
        if (col.getAttribute('data-bo-mcol') === 'open') return;
        const head = col.querySelector(':scope > .bo-mcol__title');
        if (!head) return;
        const bodyId = 'BoAcc-' + (++uid) + '-' + key;
        const body = D.createElement('div');
        body.className = 'bo-mcol__body';
        body.id = bodyId;
        body.hidden = true;
        const link = head.querySelector('a[href]');
        [...col.children].forEach((ch) => { if (ch !== head) body.appendChild(ch); });
        if (link) {
          const all = D.createElement('a');
          all.className = 'bo-mlink bo-mlink--all';
          all.href = link.getAttribute('href');
          all.textContent = (drawer.dataset.allLabel || 'All') + ' ' + link.textContent.trim().toLowerCase();
          const ul = body.querySelector('.bo-mlinks');
          if (ul) { const li = D.createElement('li'); li.appendChild(all); ul.prepend(li); } else body.prepend(all);
        }
        const t = D.createElement('button');
        t.type = 'button';
        t.className = 'bo-mcol__toggle';
        t.setAttribute('aria-expanded', 'false');
        t.setAttribute('aria-controls', bodyId);
        t.innerHTML = '<span>' + head.textContent.trim() + '</span>' + ICON_PLUS;
        t.addEventListener('click', () => {
          const on = t.getAttribute('aria-expanded') !== 'true';
          t.setAttribute('aria-expanded', on ? 'true' : 'false');
          body.hidden = !on;
        });
        col.setAttribute('data-bo-acc', '');
        col.append(t, body);
      });
      v.appendChild(copy);
      views.appendChild(v);
      if (W.BO && W.BO.init) W.BO.init(v);
      return v;
    };
    const go = (key) => {
      if (!views) return;
      const next = key ? buildView(key) : rootView;
      if (!next || next === current) return;
      const prev = current;
      if (key) {
        opener = rootView.querySelector('[data-bo-go="' + key + '"]');
        rootView.classList.add('is-behind');
        rootView.classList.remove('is-current');
        void next.offsetWidth;
        next.classList.add('is-current');
        if (opener) opener.setAttribute('aria-expanded', 'true');
        setTimeout(() => { const b = next.querySelector('.bo-drawer__back'); if (b) b.focus(); }, 60);
      } else {
        prev.classList.remove('is-current');
        rootView.classList.remove('is-behind');
        rootView.classList.add('is-current');
        if (opener) { opener.setAttribute('aria-expanded', 'false'); setTimeout(() => opener.focus(), 60); }
      }
      current = next;
    };
    const focusables = () => [...dPanel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])')]
      .filter((el) => el.offsetParent !== null && !el.closest('[hidden]') && getComputedStyle(el).visibility !== 'hidden');
    const openDrawer = () => {
      if (!drawer || dOpen) return;
      close(false);
      fillLogo();
      dOpen = true;
      clearTimeout(hideT);
      lastFocus = D.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
      hdr.querySelectorAll('[data-bo-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      D.body.classList.add('overflow-hidden', 'bo-drawer-open');
      setTimeout(() => { const c = drawer.querySelector('.bo-drawer__close'); (c || dPanel).focus(); }, 80);
    };
    const closeDrawer = (noFocus) => {
      if (!drawer || !dOpen) return;
      dOpen = false;
      drawer.classList.remove('is-open');
      hdr.querySelectorAll('[data-bo-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      D.body.classList.remove('overflow-hidden', 'bo-drawer-open');
      hideT = setTimeout(() => {
        if (dOpen) return;
        drawer.hidden = true;
        if (current !== rootView) { current.classList.remove('is-current'); rootView.classList.remove('is-behind'); rootView.classList.add('is-current'); current = rootView; }
      }, mqReduce.matches ? 0 : 520);
      if (!noFocus && lastFocus && lastFocus.focus) lastFocus.focus();
    };
    if (drawer) {
      hdr.querySelectorAll('[data-bo-drawer-open]').forEach((b) => b.addEventListener('click', openDrawer));
      drawer.querySelectorAll('[data-bo-drawer-close]').forEach((b) => b.addEventListener('click', () => closeDrawer()));
      drawer.querySelectorAll('[data-bo-go]').forEach((b) => b.addEventListener('click', () => go(b.getAttribute('data-bo-go'))));
      drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
        if (e.key !== 'Tab') return;
        const f = focusables();
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (D.activeElement === first || D.activeElement === dPanel)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && D.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      /* a "Chat with us" link closes the drawer first, so the chat window is not hidden behind it */
      drawer.addEventListener('click', (e) => { if (e.target.closest('[data-bo-chat]')) closeDrawer(true); }, true);
    }

    const onLayout = () => {
      hint();
      if (mqDesk.matches) closeDrawer(true); else close(false);
      syncTuck();
      syncFind();
      fit();
    };
    mqDesk.addEventListener('change', onLayout);
    mqPhone.addEventListener('change', onLayout);
    /* the navigation re-fits once a resize has settled (the height follows by itself, see measure) */
    let rT = 0, lastW = 0;
    W.addEventListener('resize', () => {
      clearTimeout(rT);
      rT = setTimeout(() => { const w = W.innerWidth; if (w !== lastW) { lastW = w; fit(); } }, 150);
    }, { passive: true });
    if (D.fonts && D.fonts.ready) D.fonts.ready.then(fit);
    W.addEventListener('load', fit, { once: true });
    hint();
    fit();
    if (!W.ResizeObserver) { measure(); W.addEventListener('load', measure, { once: true }); }
    syncTuck();
    syncFind();

    /* ---------- one swirl of the logo when something goes into the cart ---------- */
    D.addEventListener('bo:cart:added', () => {
      if (mqReduce.matches) return;
      const logo = hdr.querySelector('.bo-hdr__logo .bo-logo');
      if (!logo) return;
      logo.classList.remove('bo-logo--pulse');
      void logo.getBoundingClientRect();
      logo.classList.add('bo-logo--pulse');
      setTimeout(() => logo.classList.remove('bo-logo--pulse'), 650);
    });

    /* ---------- theme editor: selecting the header opens nothing; a section reload re-binds ---------- */
    /* ---------- Search box: the rolling hint, as on Raw Blend ("Search Vitamix blenders", "Search Blendtec blenders"...) ----------
       One suggestion at a time; it changes only while the box is empty, not in use, on screen and the tab is visible;
       never with reduced motion (the box then shows its plain placeholder). */
    const roll = hdr.querySelector('[data-bo-roll]');
    if (roll && !mqReduce.matches && !roll.boRolling) {
      roll.boRolling = true;
      const form = roll.closest('form');
      const words = [...roll.querySelectorAll('.bo-hdr__roll-list > span')];
      const input = form && form.querySelector('.bo-search__input');
      if (form && input && words.length > 1) {
        let k = 0, seen = true;
        words[0].classList.add('is-on');
        form.classList.add('is-rolling');
        /* on screen or not, told by an observer (reading the form's size every step forced a layout) */
        if (W.IntersectionObserver) new IntersectionObserver((es) => { seen = es[es.length - 1].isIntersecting; }).observe(form);
        const step = () => {
          if (!hdr.isConnected) return;
          const idle = !D.hidden && seen && !tucked && !form.matches(':focus-within') && input.value === '';
          if (idle) {
            const was = words[k];
            k = (k + 1) % words.length;
            words.forEach((w) => w.classList.remove('is-off'));
            was.classList.remove('is-on');
            was.classList.add('is-off');
            words[k].classList.add('is-on');
          }
          setTimeout(step, 2800);
        };
        setTimeout(step, 3200);
      }
    }

    hdr.boHeader = { open, close, openDrawer, closeDrawer, go };
  };

  const init = (scope) => (scope || D).querySelectorAll('[data-bo-header]').forEach(bind);
  window.BOHeader = { init };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
})();
